import {
  useQuery as useReactQuery,
  useMutation as useReactMutation,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "@/store/slices/loaderSlice";
import { QueryOptions } from "@/interfaces/api.interface";
import { useEffect, useRef } from "react";

export function useQuery<TData, TError = Error, TQueryKey extends QueryKey = QueryKey>(
  queryKey: TQueryKey,
  queryFn: (context: { signal: AbortSignal }) => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, "queryKey" | "queryFn"> & QueryOptions
) {
  const dispatch = useDispatch();
  const { showLoader: shouldShowLoader = true, ...queryOptions } = options || {};

  const query = useReactQuery({
    queryKey,
    queryFn: ({ signal }) => queryFn({ signal }),
    ...queryOptions,
  });

  useEffect(() => {
    if ((query.status === "pending" || query.isFetching) && shouldShowLoader) {
      dispatch(showLoader());
    }

    if (query.status !== "pending" && !query.isFetching && !query.isLoading && shouldShowLoader) {
      dispatch(hideLoader());
    }
  }, [query.status, query.isFetching, query.isLoading, shouldShowLoader, dispatch]);

  return query;
}

export function useMutation<TData, TVariables, TError = any, TContext = unknown>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, "mutationFn"> & QueryOptions
) {
  const dispatch = useDispatch();
  const { showLoader: shouldShowLoader = true, ...mutationOptions } = options || {};
  const isMutationLoaderShown = useRef(false);

  return useReactMutation({
    mutationFn,
    ...mutationOptions,
    onMutate: async (variables) => {
      if (shouldShowLoader) {
        isMutationLoaderShown.current = true;
        dispatch(showLoader());
      }

      if (mutationOptions.onMutate) {
        return await mutationOptions.onMutate(variables);
      }
    },
    onError: (error: any, variables, context) => {
      if (shouldShowLoader && isMutationLoaderShown.current) {
        isMutationLoaderShown.current = false;
        dispatch(hideLoader());
      }

      if (mutationOptions.onError) {
        mutationOptions.onError(error, variables, context);
      }
    },
    onSuccess: async (data: any, variables, context) => {
      if (shouldShowLoader && isMutationLoaderShown.current) {
        isMutationLoaderShown.current = false;
        dispatch(hideLoader());
      }

      // Check if the response contains error data
      if (data?.error || (data?.data === null && data?.statusCode >= 400)) {
        console.log("Mutation options: ", options?.onError);
        if (options?.onError) {
          // Call onError with the error data
          options?.onError(data, variables, context);
        }
        return;
      }

      if (mutationOptions.onSuccess) {
        mutationOptions.onSuccess(data, variables, context);
      }
    },
  });
}
